import { z } from 'zod';
import { AgentAssociationSchema } from './agent-association.types.js';

/**
 * Agent listeners, capability grants, and action requests: the contract for
 * an agent arming "tell me when" subscriptions for itself and asking to run
 * integration actions, gated by a permission model a human answers in the
 * agent's own thread.
 *
 * Design: `docs/internal/plans/agent-triggers-and-action-skills.md`.
 *
 * Three concepts, one spine:
 * - A **listener** is a row the agent arms: a `timer` (fire once at a time),
 *   a `signal` on a ref the agent holds (the plugin builds the filter), or a
 *   `watch` on a catalog event stream (the agent authors the filter). A
 *   fired listener is injected into the agent's conversation as a
 *   developer-role item; it is never a user-attributed prompt.
 * - A **capability matcher** names what is being asked for. A **grant** says
 *   that for a scope (`agent` / `owner` / `org`) a matcher is allowed or
 *   denied. Resolution is deny-wins, narrowest-allow, then org defaults,
 *   then a card in the thread.
 * - An **action request** records an `act` call that had to wait for a card.
 */

// --- listeners ---

export const AgentListenerKindSchema = z.enum(['timer', 'signal', 'watch']);
export type AgentListenerKind = z.infer<typeof AgentListenerKindSchema>;

export const AgentListenerStatusSchema = z.enum([
  'pending_approval',
  'armed',
  'fired',
  'cancelled',
  'denied',
  'expired',
]);
export type AgentListenerStatus = z.infer<typeof AgentListenerStatusSchema>;

/** Statuses a listener can still fire from. */
export const LIVE_LISTENER_STATUSES: ReadonlySet<AgentListenerStatus> = new Set(['armed']);

/** Statuses a listener cannot leave. */
export const TERMINAL_LISTENER_STATUSES: ReadonlySet<AgentListenerStatus> = new Set([
  'fired',
  'cancelled',
  'denied',
  'expired',
]);

/**
 * How a fire reaches the agent. `inject` places the event into the
 * conversation (a running turn reads it at its next step; an idle agent gets
 * an empty turn; a suspended box is resumed). `interrupt` interrupts a
 * running turn first, and is always gated.
 */
export const AgentListenerDeliverySchema = z.enum(['inject', 'interrupt']);
export type AgentListenerDelivery = z.infer<typeof AgentListenerDeliverySchema>;

/**
 * How wide a watch is, classified by the server from the plugin-declared
 * identity and scope paths of the event's payload. The card a human answers
 * leads with this.
 * - `object`: the filter pins one object (a PR number, an issue key).
 * - `scope`: the filter pins one container the plugin names (a repo).
 * - `org`: nothing pins it; every event of that kind in the organization.
 */
export const AgentListenerBreadthSchema = z.enum(['object', 'scope', 'org']);
export type AgentListenerBreadth = z.infer<typeof AgentListenerBreadthSchema>;

/** Upper bound on a listener's reason text, as stored and as echoed back. */
export const AGENT_LISTENER_REASON_MAX_LENGTH = 500;

export const AgentListenerSchema = z
  .object({
    id: z.string(),
    agentId: z.string(),
    organizationId: z.string(),
    /** The human the listener is attributed to: the owner at request time, or the approver. */
    requestedByUserId: z.string().nullable(),
    kind: AgentListenerKindSchema,
    status: AgentListenerStatusSchema,
    /** The agent's own words for why it armed this; echoed in every fire. */
    reason: z.string().max(AGENT_LISTENER_REASON_MAX_LENGTH),
    /** Timer only: when to fire (epoch ms). */
    fireAt: z.number().int().nullable(),
    /** Signal only: the `agent_associations` row the ref lives on. */
    refAssociationId: z.string().nullable(),
    /** Signal only: the signal name on the ref kind. */
    signal: z.string().nullable(),
    /** Signal and watch: the catalog event, materialised at arm time. */
    source: z.string().nullable(),
    event: z.string().nullable(),
    action: z.string().nullable(),
    /** Signal and watch: the stored filter (flows conditions-mode shape), or none. */
    filter: z.unknown().nullable(),
    breadth: AgentListenerBreadthSchema.nullable(),
    /** Watch only: the container the filter pins when breadth is `scope`. */
    breadthScope: z.string().nullable(),
    delivery: AgentListenerDeliverySchema,
    maxFires: z.number().int().min(1),
    fireCount: z.number().int().min(0),
    /** Signal and watch: lease end (epoch ms). Timers have none. */
    expiresAt: z.number().int().nullable(),
    notifyOnExpiry: z.boolean(),
    /** Set when a later fire's body carried this listener's expiry. */
    expiryReportedAt: z.number().int().nullable(),
    /** The pending capability request, while `status` is `pending_approval`. */
    requestId: z.string().nullable(),
    /** Summary of the last fire, for the dock row. */
    lastPayload: z.unknown().nullable(),
    lastFiredAt: z.number().int().nullable(),
    createdAt: z.number().int(),
    updatedAt: z.number().int(),
  })
  .meta({ id: 'AgentListener' });
export type AgentListener = z.infer<typeof AgentListenerSchema>;

// --- capability matchers ---

/**
 * What a ref action does to the object, declared by the plugin on every
 * action with no default. `read` and `mutate` on a ref the agent holds are
 * permissionless; `destructive` (close, merge, delete, anything another
 * action cannot undo) always asks.
 */
export const RefActionEffectSchema = z.enum(['read', 'mutate', 'destructive']);
export type RefActionEffect = z.infer<typeof RefActionEffectSchema>;

const TimerMatcherSchema = z.object({
  kind: z.literal('listen.timer'),
  /** Longest delay this matcher covers; absent covers any. */
  maxDurationMs: z.number().int().positive().optional(),
});

const SignalMatcherSchema = z.object({
  kind: z.literal('listen.signal'),
  /** Absent covers every ref kind. */
  refKind: z.string().optional(),
  /** Absent covers every signal on the ref kind. */
  signals: z.array(z.string()).min(1).optional(),
});

const WatchMatcherSchema = z.object({
  kind: z.literal('listen.watch'),
  source: z.string(),
  /** Absent covers every event of the source. */
  event: z.string().optional(),
  /** Widest breadth this matcher covers; absent covers `org`. */
  breadth: AgentListenerBreadthSchema.optional(),
  /** When breadth is `scope`: the one container it covers; absent covers any. */
  scope: z.string().optional(),
});

const InterruptMatcherSchema = z.object({
  kind: z.literal('listen.interrupt'),
});

const RefAttachMatcherSchema = z.object({
  kind: z.literal('ref.attach'),
  refKind: z.string().optional(),
});

/** An action on a ref the agent holds. */
const RefActionMatcherSchema = z.object({
  kind: z.literal('action'),
  refKind: z.string(),
  /** Absent covers every action on the ref kind. */
  actions: z.array(z.string()).min(1).optional(),
});

/** A ref-less provider operation (`create_issue`). */
const OperationMatcherSchema = z.object({
  kind: z.literal('action'),
  provider: z.string(),
  /** Absent covers every operation of the provider. */
  operation: z.string().optional(),
});

export const AgentCapabilityMatcherSchema = z
  .union([
    TimerMatcherSchema,
    SignalMatcherSchema,
    WatchMatcherSchema,
    InterruptMatcherSchema,
    RefAttachMatcherSchema,
    RefActionMatcherSchema,
    OperationMatcherSchema,
  ])
  .meta({ id: 'AgentCapabilityMatcher' });
export type AgentCapabilityMatcher = z.infer<typeof AgentCapabilityMatcherSchema>;

/**
 * A concrete request, as the agent made it. The same shape as a matcher but
 * fully specified: a request never leaves a field open.
 */
export const AgentCapabilityRequestSchema = z.union([
  z.object({ kind: z.literal('listen.timer'), durationMs: z.number().int().positive() }),
  z.object({ kind: z.literal('listen.signal'), refKind: z.string(), signal: z.string() }),
  z.object({
    kind: z.literal('listen.watch'),
    source: z.string(),
    event: z.string(),
    breadth: AgentListenerBreadthSchema,
    scope: z.string().optional(),
  }),
  z.object({ kind: z.literal('listen.interrupt') }),
  z.object({ kind: z.literal('ref.attach'), refKind: z.string() }),
  z.object({ kind: z.literal('action'), refKind: z.string(), action: z.string() }),
  z.object({ kind: z.literal('action'), provider: z.string(), operation: z.string() }),
]);
export type AgentCapabilityRequest = z.infer<typeof AgentCapabilityRequestSchema>;

const BREADTH_RANK: Record<AgentListenerBreadth, number> = { object: 0, scope: 1, org: 2 };

/**
 * Whether a grant's matcher covers a concrete request. A matcher with a
 * field left open covers any value of it; a matcher that names a field
 * covers only that value. Breadth is ordered: a matcher at `scope` covers
 * `object` and `scope` requests, never `org`.
 */
export function matcherCovers(
  matcher: AgentCapabilityMatcher,
  request: AgentCapabilityRequest,
): boolean {
  if (matcher.kind !== request.kind) return false;
  switch (matcher.kind) {
    case 'listen.timer': {
      if (request.kind !== 'listen.timer') return false;
      return matcher.maxDurationMs === undefined || request.durationMs <= matcher.maxDurationMs;
    }
    case 'listen.signal': {
      if (request.kind !== 'listen.signal') return false;
      if (matcher.refKind !== undefined && matcher.refKind !== request.refKind) return false;
      return matcher.signals === undefined || matcher.signals.includes(request.signal);
    }
    case 'listen.watch': {
      if (request.kind !== 'listen.watch') return false;
      if (matcher.source !== request.source) return false;
      if (matcher.event !== undefined && matcher.event !== request.event) return false;
      const allowed = matcher.breadth ?? 'org';
      if (BREADTH_RANK[request.breadth] > BREADTH_RANK[allowed]) return false;
      if (matcher.scope !== undefined) {
        // A scoped matcher covers only requests inside that container. An
        // `org` request has no container, so it is never covered here.
        return request.breadth !== 'org' && request.scope === matcher.scope;
      }
      return true;
    }
    case 'listen.interrupt':
      return request.kind === 'listen.interrupt';
    case 'ref.attach': {
      if (request.kind !== 'ref.attach') return false;
      return matcher.refKind === undefined || matcher.refKind === request.refKind;
    }
    case 'action': {
      if (request.kind !== 'action') return false;
      if ('refKind' in matcher) {
        if (!('refKind' in request) || matcher.refKind !== request.refKind) return false;
        return matcher.actions === undefined || matcher.actions.includes(request.action);
      }
      if (!('provider' in request) || matcher.provider !== request.provider) return false;
      return matcher.operation === undefined || matcher.operation === request.operation;
    }
  }
}

/**
 * The matcher that covers exactly one request and nothing else. This is what
 * an "always allow" answer writes, so approving `set_state` on `linear:issue`
 * never grants `assign`; widening is a separate, explicit toggle.
 */
export function exactMatcherFor(request: AgentCapabilityRequest): AgentCapabilityMatcher {
  switch (request.kind) {
    case 'listen.timer':
      return { kind: 'listen.timer', maxDurationMs: request.durationMs };
    case 'listen.signal':
      return { kind: 'listen.signal', refKind: request.refKind, signals: [request.signal] };
    case 'listen.watch':
      return {
        kind: 'listen.watch',
        source: request.source,
        event: request.event,
        breadth: request.breadth,
        ...(request.scope !== undefined ? { scope: request.scope } : {}),
      };
    case 'listen.interrupt':
      return { kind: 'listen.interrupt' };
    case 'ref.attach':
      return { kind: 'ref.attach', refKind: request.refKind };
    case 'action':
      return 'refKind' in request
        ? { kind: 'action', refKind: request.refKind, actions: [request.action] }
        : { kind: 'action', provider: request.provider, operation: request.operation };
  }
}

/**
 * The one-step-wider matcher an "always allow" can be widened to, with the
 * label the card shows for the toggle; null when the request has no wider
 * form worth offering. Widening is always explicit: the exact matcher is
 * the default, and this is the secondary toggle the plan describes ("any
 * repo", "all Linear issue actions").
 */
export function widenedMatcherFor(
  request: AgentCapabilityRequest,
): { matcher: AgentCapabilityMatcher; label: string } | null {
  switch (request.kind) {
    case 'listen.timer':
    case 'listen.interrupt':
      return null;
    case 'listen.signal':
      return {
        matcher: { kind: 'listen.signal', refKind: request.refKind },
        label: `every signal on ${request.refKind}`,
      };
    case 'listen.watch':
      if (request.breadth !== 'org' && request.scope !== undefined) {
        return {
          matcher: {
            kind: 'listen.watch',
            source: request.source,
            event: request.event,
            breadth: request.breadth,
          },
          label: `${request.event} in any ${request.breadth === 'object' ? 'container' : 'scope'} on ${request.source}`,
        };
      }
      return {
        matcher: { kind: 'listen.watch', source: request.source, breadth: request.breadth },
        label: `every ${request.source} event at this breadth`,
      };
    case 'ref.attach':
      return { matcher: { kind: 'ref.attach' }, label: 'attaching any ref kind' };
    case 'action':
      return 'refKind' in request
        ? {
            matcher: { kind: 'action', refKind: request.refKind },
            label: `every action on ${request.refKind}`,
          }
        : {
            matcher: { kind: 'action', provider: request.provider },
            label: `every ${request.provider} operation`,
          };
  }
}

// --- grants ---

/**
 * Whom a grant applies to, narrowest first. `agent` is one agent for its
 * life; `owner` is every agent a user launches; `org` is every agent in the
 * organization.
 */
export const AgentCapabilityGrantScopeSchema = z.enum(['agent', 'owner', 'org']);
export type AgentCapabilityGrantScope = z.infer<typeof AgentCapabilityGrantScopeSchema>;

export const GRANT_SCOPE_ORDER: readonly AgentCapabilityGrantScope[] = ['agent', 'owner', 'org'];

export const AgentCapabilityGrantEffectSchema = z.enum(['allow', 'deny']);
export type AgentCapabilityGrantEffect = z.infer<typeof AgentCapabilityGrantEffectSchema>;

export const AgentCapabilityGrantSchema = z
  .object({
    id: z.string(),
    organizationId: z.string(),
    scope: AgentCapabilityGrantScopeSchema,
    /** The agent id, user id, or organization id the scope names. */
    subjectId: z.string(),
    matcher: AgentCapabilityMatcherSchema,
    effect: AgentCapabilityGrantEffectSchema,
    grantedByUserId: z.string().nullable(),
    expiresAt: z.number().int().nullable(),
    createdAt: z.number().int(),
  })
  .meta({ id: 'AgentCapabilityGrant' });
export type AgentCapabilityGrant = z.infer<typeof AgentCapabilityGrantSchema>;

/** What the resolver decided: run it, refuse it, or put a card in the thread. */
export const AgentCapabilityDecisionSchema = z.enum(['allow', 'deny', 'ask']);
export type AgentCapabilityDecision = z.infer<typeof AgentCapabilityDecisionSchema>;

/**
 * Org defaults consulted when no grant matches. Every field is a decision,
 * so an org can make the whole feature ask or make more of it silent.
 */
export const AgentCapabilityDefaultsSchema = z.object({
  /** Timers at or under this delay are allowed; longer ones ask. */
  timerAutoAllowMaxMs: z.number().int().positive(),
  /** Signals on refs the agent holds. */
  signal: AgentCapabilityDecisionSchema,
  /** Watches, at any breadth. */
  watch: AgentCapabilityDecisionSchema,
  interrupt: AgentCapabilityDecisionSchema,
  refAttach: AgentCapabilityDecisionSchema,
  /** Ref actions by their declared effect. */
  actionRead: AgentCapabilityDecisionSchema,
  actionMutate: AgentCapabilityDecisionSchema,
  actionDestructive: AgentCapabilityDecisionSchema,
  /** Ref-less provider operations. */
  operation: AgentCapabilityDecisionSchema,
});
export type AgentCapabilityDefaults = z.infer<typeof AgentCapabilityDefaultsSchema>;

export const DEFAULT_AGENT_CAPABILITY_DEFAULTS: AgentCapabilityDefaults = {
  timerAutoAllowMaxMs: 24 * 60 * 60 * 1000,
  signal: 'allow',
  watch: 'ask',
  interrupt: 'ask',
  refAttach: 'allow',
  actionRead: 'allow',
  actionMutate: 'allow',
  actionDestructive: 'ask',
  operation: 'ask',
};

/**
 * Facts about the request the defaults need that the request itself does
 * not carry: a ref action's declared effect.
 */
export interface AgentCapabilityRequestContext {
  /** For `action` requests on a ref: the effect the ref kind declared. */
  actionEffect?: RefActionEffect;
}

/**
 * The decision the org defaults give a request when no grant covers it.
 */
export function defaultDecision(
  request: AgentCapabilityRequest,
  defaults: AgentCapabilityDefaults,
  context: AgentCapabilityRequestContext = {},
): AgentCapabilityDecision {
  switch (request.kind) {
    case 'listen.timer':
      return request.durationMs <= defaults.timerAutoAllowMaxMs ? 'allow' : 'ask';
    case 'listen.signal':
      return defaults.signal;
    case 'listen.watch':
      return defaults.watch;
    case 'listen.interrupt':
      return defaults.interrupt;
    case 'ref.attach':
      return defaults.refAttach;
    case 'action': {
      if (!('refKind' in request)) return defaults.operation;
      switch (context.actionEffect) {
        case 'read':
          return defaults.actionRead;
        case 'mutate':
          return defaults.actionMutate;
        case 'destructive':
          return defaults.actionDestructive;
        default:
          // An action whose effect the caller could not determine is
          // treated as destructive: the registry refuses to load one
          // without an effect, so this only happens on a stale row.
          return defaults.actionDestructive;
      }
    }
  }
}

/**
 * Resolve a request against the grants that apply to it. Order:
 * 1. any unexpired `deny` whose matcher covers the request wins;
 * 2. otherwise the narrowest-scope unexpired `allow` that covers it;
 * 3. otherwise the org defaults.
 *
 * Grants are passed already filtered to the agent, its owner, and its org
 * (the caller reads them by subject); this function only checks matchers,
 * effects, and expiry, and is pure so it can be tested without a database.
 */
export function resolveCapability(
  request: AgentCapabilityRequest,
  grants: readonly AgentCapabilityGrant[],
  defaults: AgentCapabilityDefaults,
  options: { now: number; context?: AgentCapabilityRequestContext },
): { decision: AgentCapabilityDecision; grant: AgentCapabilityGrant | null } {
  const live = grants.filter(
    (g) => (g.expiresAt === null || g.expiresAt > options.now) && matcherCovers(g.matcher, request),
  );
  const deny = live.find((g) => g.effect === 'deny');
  if (deny) return { decision: 'deny', grant: deny };
  const allows = live.filter((g) => g.effect === 'allow');
  if (allows.length > 0) {
    const narrowest = [...allows].sort(
      (a, b) => GRANT_SCOPE_ORDER.indexOf(a.scope) - GRANT_SCOPE_ORDER.indexOf(b.scope),
    )[0]!;
    return { decision: 'allow', grant: narrowest };
  }
  return { decision: defaultDecision(request, defaults, options.context), grant: null };
}

// --- capability requests (the card) and action requests ---

/**
 * The answers a card offers. `allow_once` and `deny` resolve this request
 * only; `allow_agent` also writes an `agent`-scope allow, `allow_always` an
 * `owner`-scope allow, and `never` an `owner`-scope deny, each for the exact
 * matcher the agent asked with.
 */
export const AgentCapabilityAnswerSchema = z.enum([
  'allow_once',
  'allow_agent',
  'allow_always',
  'deny',
  'never',
]);
export type AgentCapabilityAnswer = z.infer<typeof AgentCapabilityAnswerSchema>;

export const AgentActionRequestStatusSchema = z.enum([
  'pending_approval',
  'executed',
  'denied',
  'failed',
  'expired',
]);
export type AgentActionRequestStatus = z.infer<typeof AgentActionRequestStatusSchema>;

export const AgentActionRequestSchema = z
  .object({
    id: z.string(),
    agentId: z.string(),
    organizationId: z.string(),
    refAssociationId: z.string().nullable(),
    provider: z.string(),
    operation: z.string(),
    /** The bound params: the plugin's identity params plus the agent's. */
    params: z.record(z.string(), z.unknown()),
    status: AgentActionRequestStatusSchema,
    requestId: z.string().nullable(),
    result: z.unknown().nullable(),
    error: z.string().nullable(),
    createdAt: z.number().int(),
    resolvedAt: z.number().int().nullable(),
  })
  .meta({ id: 'AgentActionRequest' });
export type AgentActionRequest = z.infer<typeof AgentActionRequestSchema>;

// --- refs (the attached set, as the route and the dock see it) ---

export const RefSignalDescriptorSchema = z.object({
  name: z.string(),
  description: z.string(),
  event: z.object({ source: z.string(), event: z.string(), action: z.string().optional() }),
  /** Whether the plugin can read the live object to verify a filter. */
  probeable: z.boolean(),
  usuallyFinal: z.boolean(),
  default: z.boolean(),
});
export type RefSignalDescriptor = z.infer<typeof RefSignalDescriptorSchema>;

export const RefActionDescriptorSchema = z.object({
  name: z.string(),
  description: z.string(),
  operation: z.object({ provider: z.string(), operation: z.string() }),
  effect: RefActionEffectSchema,
  /** Params the agent supplies (the operation's schema minus the bound identity). */
  paramsJsonSchema: z.record(z.string(), z.unknown()).nullable(),
});
export type RefActionDescriptor = z.infer<typeof RefActionDescriptorSchema>;

export const RefKindDescriptorSchema = z
  .object({
    kind: z.string(),
    label: z.string(),
    associationKind: z.string(),
    signals: z.array(RefSignalDescriptorSchema),
    actions: z.array(RefActionDescriptorSchema),
  })
  .meta({ id: 'RefKindDescriptor' });
export type RefKindDescriptor = z.infer<typeof RefKindDescriptorSchema>;

/** One attached ref: the association row plus the handle its kind prints. */
export const AgentRefSchema = z
  .object({
    kind: z.string(),
    handle: z.string(),
    association: AgentAssociationSchema,
  })
  .meta({ id: 'AgentRef' });
export type AgentRef = z.infer<typeof AgentRefSchema>;

export const ListAgentRefsResponseSchema = z
  .object({
    refs: z.array(AgentRefSchema),
    /** Every ref kind the deployment knows, so a client can describe a ref without a second call. */
    kinds: z.array(RefKindDescriptorSchema),
  })
  .meta({ id: 'ListAgentRefsResponse' });
export type ListAgentRefsResponse = z.infer<typeof ListAgentRefsResponseSchema>;

// --- listener routes, events, and org defaults ---

/** WebSocket `flow_event` type pushed on every listener status change. */
export const AGENT_LISTENER_WS_EVENT = 'agent_listener:updated';

/** Default and maximum lease on a signal or watch listener. */
export const DEFAULT_LISTENER_LEASE_MS = 7 * 24 * 60 * 60 * 1000;
export const MAX_LISTENER_LEASE_MS = 30 * 24 * 60 * 60 * 1000;
/** Furthest a timer may be set. */
export const MAX_TIMER_DELAY_MS = 30 * 24 * 60 * 60 * 1000;
/** Most armed listeners one agent may hold. */
export const MAX_ARMED_LISTENERS_PER_AGENT = 20;
/** Upper bound on `maxFires` for a recurring listener. */
export const MAX_LISTENER_FIRES = 50;
/** Terminal listener rows are deleted after this long. */
export const LISTENER_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

export const ListAgentListenersResponseSchema = z
  .object({ listeners: z.array(AgentListenerSchema) })
  .meta({ id: 'ListAgentListenersResponse' });
export type ListAgentListenersResponse = z.infer<typeof ListAgentListenersResponseSchema>;

export const RenewAgentListenerRequestSchema = z
  .object({
    /** New lease length from now (ms); omitted means the default lease. */
    forMs: z.number().int().positive().max(MAX_LISTENER_LEASE_MS).optional(),
  })
  .meta({ id: 'RenewAgentListenerRequest' });
export type RenewAgentListenerRequest = z.infer<typeof RenewAgentListenerRequestSchema>;

export const AgentListenerResponseSchema = z
  .object({ listener: AgentListenerSchema })
  .meta({ id: 'AgentListenerResponse' });
export type AgentListenerResponse = z.infer<typeof AgentListenerResponseSchema>;

export const AgentCapabilityDecisionRequestSchema = z
  .object({
    answer: AgentCapabilityAnswerSchema,
    /** Write the standing grant for the one-step-wider matcher (`widenedMatcherFor`). */
    widen: z.boolean().optional(),
  })
  .meta({ id: 'AgentCapabilityDecisionRequest' });
export type AgentCapabilityDecisionRequest = z.infer<typeof AgentCapabilityDecisionRequestSchema>;

// --- on-box tools (reflex-service ref | listen | act over the bridge relay) ---

export const AGENT_LISTENER_TOOL_PREFIXES = { ref: 'ref', listen: 'listen', act: 'act' } as const;

export const AGENT_LISTENER_TOOLS = {
  refList: 'ref_list',
  refDescribe: 'ref_describe',
  refAttach: 'ref_attach',
  listenRequest: 'listen_request',
  listenList: 'listen_list',
  listenRenew: 'listen_renew',
  listenCheck: 'listen_check',
  listenCancel: 'listen_cancel',
  actList: 'act_list',
  actRun: 'act_run',
} as const;
export type AgentListenerTool = (typeof AGENT_LISTENER_TOOLS)[keyof typeof AGENT_LISTENER_TOOLS];

/** `30s`, `5m`, `2h`, `3d`, or a plain number of seconds; null on anything else. */
export function parseDurationMs(spec: string): number | null {
  const m = /^(\d+)([smhd]?)$/.exec(spec.trim());
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  const unit = m[2] || 's';
  const factor = { s: 1000, m: 60_000, h: 3_600_000, d: 86_400_000 }[unit as 's' | 'm' | 'h' | 'd'];
  return n * factor;
}

const DurationSchema = z.string().min(1).max(20);
const HandleSchema = z.string().min(1).max(500);
const ReasonSchema = z.string().min(1).max(AGENT_LISTENER_REASON_MAX_LENGTH);

export const ListenWhereSchema = z.object({
  path: z.string().min(1).max(200),
  op: z
    .enum([
      'equals',
      'not_equals',
      'contains',
      'in',
      'intersects',
      'exists',
      'not_exists',
      'truthy',
    ])
    .optional(),
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]).optional(),
});

export const ListenRequestParamsSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('timer'),
    in: DurationSchema.optional(),
    at: z.string().optional(),
    reason: ReasonSchema,
    interrupt: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('signal'),
    ref: HandleSchema,
    signal: z.string().min(1).max(100),
    reason: ReasonSchema,
    everyTime: z.boolean().optional(),
    for: DurationSchema.optional(),
    notifyExpiry: z.boolean().optional(),
    interrupt: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('watch'),
    source: z.string().min(1).max(100),
    event: z.string().min(1).max(100),
    action: z.string().min(1).max(100).optional(),
    where: z.array(ListenWhereSchema).max(20).optional(),
    reason: ReasonSchema,
    everyTime: z.boolean().optional(),
    for: DurationSchema.optional(),
    notifyExpiry: z.boolean().optional(),
    interrupt: z.boolean().optional(),
  }),
]);
export type ListenRequestParams = z.infer<typeof ListenRequestParamsSchema>;

export const ListenIdParamsSchema = z.object({ id: z.string().min(1) });
export const ListenRenewParamsSchema = z.object({
  id: z.string().min(1),
  for: DurationSchema.optional(),
});
export const RefHandleParamsSchema = z.object({
  handle: HandleSchema,
  reason: z.string().max(500).optional(),
});
export const ActRunParamsSchema = z
  .object({
    ref: HandleSchema.optional(),
    action: z.string().min(1).max(100).optional(),
    provider: z.string().min(1).max(100).optional(),
    operation: z.string().min(1).max(100).optional(),
    params: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((p) => (p.ref && p.action) || (p.provider && p.operation), {
    message: 'pass ref + action, or provider + operation',
  });
export type ActRunParams = z.infer<typeof ActRunParamsSchema>;

/** What every mutating tool answers; `pending` means keep working. */
export const ListenerToolOutcomeSchema = z.union([
  z.object({
    status: z.literal('granted'),
    listener: AgentListenerSchema.optional(),
    result: z.unknown().optional(),
  }),
  z.object({
    status: z.literal('pending'),
    requestId: z.string(),
    listener: AgentListenerSchema.optional(),
  }),
  z.object({ status: z.literal('denied'), reason: z.string() }),
]);
export type ListenerToolOutcome = z.infer<typeof ListenerToolOutcomeSchema>;

export const AgentCapabilityDecisionResponseSchema = z
  .union([
    z.object({ listener: AgentListenerSchema }),
    z.object({ actionRequest: AgentActionRequestSchema }),
  ])
  .meta({ id: 'AgentCapabilityDecisionResponse' });
export type AgentCapabilityDecisionResponse = z.infer<typeof AgentCapabilityDecisionResponseSchema>;
